import { z } from 'zod'

/**
 * What is wrong with a report, decided once for both sides of the wire.
 *
 * A report is never refused. A technician standing in a subfloor with one bar
 * of signal is the wrong person to argue with about a blank field, and the
 * mockup says so: the button reads "Complete anyway". So everything here is a
 * warning that the filler shows before sending and the API records at
 * submission — nobody is stopped, and nobody can later claim they were not
 * told.
 *
 * Three groups, in the order the sheet lists them:
 *
 * - **Contradictions** — answers that disagree with each other. Live termites
 *   with an undetected risk of Low is the example that matters, because each
 *   answer is defensible alone and the pair is not. These are per-form rules on
 *   `FormVersion.checks`, declarative so that this one evaluator serves the
 *   filler and the submit endpoint and the two cannot drift.
 * - **Vague** — free text too short to mean anything on its own. An auditor
 *   reads the line without the property in front of them, so "mud" is not an
 *   answer where "mud trails to the eastern pier" is. Opt-in per field via
 *   `FormField.minLength`.
 * - **Blank** — no answer at all.
 *
 * Deliberately not validation. `FormField.validation` refuses bad input as it
 * is typed; this judges a finished report as a reader would.
 */

/** Mirrors `FormFieldType` without importing the generated Prisma client. */
export type ReportFieldType =
  | 'TEXT'
  | 'TEXTAREA'
  | 'NUMBER'
  | 'DECIMAL'
  | 'BOOLEAN'
  | 'DATE'
  | 'DATETIME'
  | 'SELECT'
  | 'MULTI_SELECT'
  | 'PHOTO'
  | 'SIGNATURE'
  | 'PASS_FAIL'
  | 'RATING'
  | 'HEADING'

/**
 * One answer, in the shape the filler holds it.
 *
 * A multi-select and a photo field are both arrays — of chosen options and of
 * attachment ids respectively — so one `filled` rule covers both.
 */
export type ReportAnswer = string | string[] | number | boolean | null | undefined

export type ReportAnswers = Record<string, ReportAnswer>

/**
 * Whether an answer counts as given.
 *
 * `false` counts. It is the answer to "was a nest found", and treating it as
 * absent would nag a technician to fill in a field they have already answered
 * correctly. Only null, undefined, empty string and empty array are blank.
 */
export function isAnswered(value: ReportAnswer): boolean {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  return true
}

/** Answers rendered as one string, for length and pattern rules. */
function asText(value: ReportAnswer): string {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.join(', ')
  return String(value)
}

export const formConditionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('equals'), field: z.string().min(1), value: z.string() }),
  z.object({ kind: z.literal('includes'), field: z.string().min(1), value: z.string() }),
  z.object({ kind: z.literal('blank'), field: z.string().min(1) }),
  z.object({ kind: z.literal('answered'), field: z.string().min(1) }),
  z.object({ kind: z.literal('matches'), field: z.string().min(1), pattern: z.string().min(1) }),
  z.object({ kind: z.literal('chosenMoreThan'), field: z.string().min(1), count: z.number().int() }),
  /** Any one of several answers: "High or Extreme". */
  z.object({ kind: z.literal('oneOf'), field: z.string().min(1), values: z.array(z.string()).min(1) }),
  /** Anything but this answer, including no answer yet. */
  z.object({ kind: z.literal('notEquals'), field: z.string().min(1), value: z.string() }),
])

export const formCheckRuleSchema = z.object({
  /** Every condition must hold for the rule to fire. */
  when: z.array(formConditionSchema).min(1),
  /** The field the check sheet jumps to. */
  field: z.string().min(1),
  title: z.string().min(1),
  detail: z.string().min(1),
})

export const formCheckRulesSchema = z.array(formCheckRuleSchema)

export type FormCheckCondition = z.infer<typeof formConditionSchema>
export type FormCheckRule = z.infer<typeof formCheckRuleSchema>

/**
 * Reads `FormVersion.checks` without trusting it.
 *
 * The column is JSON, which means a hand-edited row or an older shape can put
 * anything in it. A malformed rule set yields no checks rather than an
 * exception, because a report that cannot be sent is a worse outcome than a
 * check that did not run.
 */
export function parseCheckRules(raw: unknown): FormCheckRule[] {
  if (raw === null || raw === undefined) return []
  const parsed = formCheckRulesSchema.safeParse(raw)
  return parsed.success ? parsed.data : []
}

/**
 * Whether one condition holds against the answers.
 *
 * Shared by the cross-field checks and by `visibleWhen`, so "shown when live
 * termites were found" and "warn when live termites were found" read the
 * answers the same way.
 */
export function conditionHolds(condition: FormCheckCondition, answers: ReportAnswers): boolean {
  const value = answers[condition.field]

  switch (condition.kind) {
    case 'equals':
      return isAnswered(value) && asText(value) === condition.value
    case 'includes':
      return Array.isArray(value) ? value.includes(condition.value) : asText(value) === condition.value
    case 'blank':
      return !isAnswered(value)
    case 'answered':
      return isAnswered(value)
    case 'matches':
      try {
        return new RegExp(condition.pattern, 'i').test(asText(value))
      } catch {
        // A rule with a broken pattern simply does not fire.
        return false
      }
    case 'chosenMoreThan':
      return Array.isArray(value) && value.length > condition.count
    case 'oneOf':
      return Array.isArray(value)
        ? value.some((item) => condition.values.includes(item))
        : isAnswered(value) && condition.values.includes(asText(value))
    case 'notEquals':
      return !isAnswered(value) || asText(value) !== condition.value
  }
}

export type ReportFindingGroup = 'CONTRADICTION' | 'VAGUE' | 'BLANK'

export type ReportFinding = {
  group: ReportFindingGroup
  /** The field to scroll to when the row is tapped. */
  fieldKey: string
  title: string
  detail: string
}

/** The subset of a field the review needs. */
export type ReportFieldSpec = {
  key: string
  label: string
  type: ReportFieldType
  /** The section heading, shown under a blank so it is findable. */
  sectionName?: string | null
  /** Below this, free text reads as no answer at all. Null means never vague. */
  minLength?: number | null
  /** `FormField.visibleWhen`. A question whose conditions do not hold is not asked. */
  visibleWhen?: unknown
}

/**
 * `FormField.visibleWhen`: the conditions under which a question is asked.
 *
 * A list of the same conditions the checks use, all of which must hold. The
 * follow-up questions after a "not found" sat blank on 93% of Bug Busters'
 * termite reports; asking them only after a "found" is most of what makes the
 * form short (ADR 029).
 */
export const visibleWhenSchema = z.array(formConditionSchema).min(1)

/**
 * Reads `visibleWhen` without trusting it. Missing or malformed means always
 * asked, because hiding a question by accident loses a finding and showing
 * one by accident costs a tap.
 */
export function parseVisibleWhen(raw: unknown): FormCheckCondition[] | null {
  if (raw === null || raw === undefined) return null
  const parsed = visibleWhenSchema.safeParse(raw)
  return parsed.success ? parsed.data : null
}

/**
 * Whether a question is asked, given the answers so far.
 *
 * One evaluator for the filler, the check sheet and the PDF, which is what
 * lets a hidden question go unprinted: the page can only leave out what the
 * screen did not ask (the amendment to decision 78 in ADR 029).
 */
export function isFieldVisible(visibleWhen: unknown, answers: ReportAnswers): boolean {
  const conditions = parseVisibleWhen(visibleWhen)
  if (conditions === null) return true
  return conditions.every((condition) => conditionHolds(condition, answers))
}

/**
 * Everything a reader would query about a report, in the order the sheet shows
 * it: what contradicts, what is too vague, what is missing.
 */
export function reviewReport(
  fields: ReportFieldSpec[],
  answers: ReportAnswers,
  rules: FormCheckRule[],
): ReportFinding[] {
  const contradictions: ReportFinding[] = rules
    .filter((rule) => rule.when.every((condition) => conditionHolds(condition, answers)))
    .map((rule) => ({
      group: 'CONTRADICTION' as const,
      fieldKey: rule.field,
      title: rule.title,
      detail: rule.detail,
    }))

  const vague: ReportFinding[] = []
  const blank: ReportFinding[] = []

  for (const field of fields) {
    // A heading is not a question, and a question nobody was asked is not blank.
    if (field.type === 'HEADING') continue
    if (!isFieldVisible(field.visibleWhen, answers)) continue

    const value = answers[field.key]

    if (!isAnswered(value)) {
      blank.push({
        group: 'BLANK',
        fieldKey: field.key,
        title: field.label,
        detail: field.sectionName ?? '',
      })
      continue
    }

    const text = asText(value).trim()
    if (field.minLength != null && text.length < field.minLength) {
      vague.push({
        group: 'VAGUE',
        fieldKey: field.key,
        title: field.label,
        detail: `“${text}” — name the location and what you saw. An auditor reads this line on its own.`,
      })
    }
  }

  return [...contradictions, ...vague, ...blank]
}

/** How far through a report is, for the progress bar and the sheet's subtitle. */
export function reportProgress(
  fields: ReportFieldSpec[],
  answers: ReportAnswers,
): { answered: number; total: number; percent: number } {
  const asked = fields.filter(
    (field) => field.type !== 'HEADING' && isFieldVisible(field.visibleWhen, answers),
  )
  const answered = asked.filter((field) => isAnswered(answers[field.key])).length
  const total = asked.length
  return {
    answered,
    total,
    percent: total === 0 ? 0 : Math.round((answered / total) * 100),
  }
}
